import styled from "@emotion/styled"
import type { PTextProps, SegmentedControlChangeEventDetail } from "@porsche-design-system/components-react"
import { PButton, PSegmentedControl, PSegmentedControlItem, PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import { useCallback, useMemo } from "react"
import type { JSX, PropsWithChildren } from "react"
import { useController, useFormContext } from "react-hook-form"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import type { MessageDescriptor } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup, CheckboxGroupField, TextAreaField } from "../form"
import type { CheckboxGroupItem } from "../form"
import type { FeedbackFormValues } from "./FeedbackFormValues"

const messages = defineMessages({
	heading: {
		defaultMessage: "Feedback",
		id: "feedbackForm.heading",
	},
	question1: {
		defaultMessage: "How satisfied are you with the information presented on this page?",
		id: "feedbackForm.question1",
	},
	question2: {
		defaultMessage: "What can we do to improve this page?",
		id: "feedbackForm.question2",
	},
	question3: {
		defaultMessage: "Which role do you have at the Porsche Center?",
		id: "feedbackForm.question3",
	},

	dissatisfied: {
		defaultMessage: "Very dissatisfied",
		id: "feedbackForm.dissatisfied",
	},
	satisfied: {
		defaultMessage: "Very satisfied",
		id: "feedbackForm.satisfied",
	},

	cta: {
		defaultMessage: "Send feedback",
		id: "feedbackForm.cta",
	},
})

const roleMessages = defineMessages({
	dispatcher: {
		defaultMessage: "Dispatcher",
		id: "feedbackForm.roleDispatcher",
	},
	other: {
		defaultMessage: "Other",
		id: "feedbackForm.roleOther",
	},
	salesConsultantNewCar: {
		defaultMessage: "Sales Consultant New Car",
		id: "feedbackForm.roleSalesConsultantNewCar",
	},
	salesConsultantUsedCar: {
		defaultMessage: "Sales Consultant Used Car",
		id: "feedbackForm.roleSalesConsultantUsedCar",
	},
	salesManager: {
		defaultMessage: "Sales Manager",
		id: "feedbackForm.roleSalesManager",
	},
})

const getRoleOptions = (formatMessage: (msg: MessageDescriptor) => string): CheckboxGroupItem[] => [
	{ label: formatMessage(roleMessages.salesManager), value: "salesManager" },
	{ label: formatMessage(roleMessages.salesConsultantNewCar), value: "salesConsultantNewCar" },
	{ label: formatMessage(roleMessages.salesConsultantUsedCar), value: "salesConsultantUsedCar" },
	{ label: formatMessage(roleMessages.dispatcher), value: "dispatcher" },
	{ label: formatMessage(roleMessages.other), value: "other" },
]

interface FeedbackFormProps {
	error?: JSX.Element
	onClose?: () => void
}

export const FeedbackForm = ({ error, onClose }: FeedbackFormProps) => {
	const { formatMessage } = useIntl()
	const {
		control,
		formState: { isSubmitting },
	} = useFormContext<FeedbackFormValues>()

	const roleOptions = useMemo(() => getRoleOptions(formatMessage), [formatMessage])

	const {
		field: { onChange: onRatingChange, value: rating },
		fieldState: { error: ratingError },
	} = useController({
		control,
		name: "rating",
		rules: {
			required: {
				message: formatMessage(formMessages.required),
				value: true,
			},
		},
		shouldUnregister: true,
	})

	const handleRatingChange = useCallback(
		(e: CustomEvent<SegmentedControlChangeEventDetail>) =>
			onRatingChange(e.detail.value as FeedbackFormValues["rating"]),
		[onRatingChange],
	)

	return (
		<FormWrapper>
			<div>
				<StyledText>
					<FormattedMessage {...messages.question1} /> *
				</StyledText>
				<RatingWrapper>
					<RatingLabel>
						<FormattedMessage {...messages.dissatisfied} />
					</RatingLabel>

					<StyledSegmentedControl
						state={ratingError?.message ? "error" : undefined}
						message={ratingError?.message}
						columns={5}
						disabled={isSubmitting}
						value={rating}
						onChange={handleRatingChange}
					>
						{Array.from({ length: 5 }, (_, i) => (
							<PSegmentedControlItem key={i} disabled={isSubmitting} value={i + 1}>
								{i + 1}
							</PSegmentedControlItem>
						))}
					</StyledSegmentedControl>

					<RatingLabelRight>
						<FormattedMessage {...messages.satisfied} />
					</RatingLabelRight>
				</RatingWrapper>
			</div>

			<div>
				<StyledText>
					<FormattedMessage {...messages.question2} />
				</StyledText>
				<TextAreaField disabledHint={isSubmitting ? "" : undefined} name="text" maxLength={500} />
			</div>

			<div>
				<StyledText>
					<FormattedMessage {...messages.question3} />
				</StyledText>
				<CheckboxGroupField disabledHint={isSubmitting ? "" : undefined} name="roles" items={roleOptions} />
			</div>

			{error}

			<ButtonGroup withoutMarginTop>
				<PButton type="submit" loading={isSubmitting}>
					<FormattedMessage {...messages.cta} />
				</PButton>
				<PButton type="button" variant="secondary" disabled={isSubmitting} onClick={onClose}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
		</FormWrapper>
	)
}

const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticLg};
`

const StyledText = styled(({ children, ...rest }: PropsWithChildren<PTextProps>) => (
	<PText weight="semibold" {...rest}>
		{children}
	</PText>
))`
	margin: 0 0 ${spacingStaticMd};
`

const RatingWrapper = styled.div`
	display: grid;
	gap: ${spacingStaticSm};
	grid-template-columns: 1fr 1fr;

	@media (min-width: ${breakpoint.s}px) {
		display: flex;
		align-items: flex-start;
		gap: ${spacingStaticMd};
	}
`

// margin-top aligns the label text with the center of the segmented control buttons
const RatingLabel = styled(PText)`
	white-space: nowrap;
	@media (min-width: ${breakpoint.s}px) {
		margin-top: ${spacingStaticMd};
	}
	`

const RatingLabelRight = styled(PText)`
	white-space: nowrap;
	justify-self: end;

	@media (min-width: ${breakpoint.s}px) {
		margin-top: ${spacingStaticMd};
	}
`

const StyledSegmentedControl = styled(PSegmentedControl)`
	@media (max-width: ${breakpoint.s}px) {
		grid-row: 1;
		grid-column: 1 / -1;
	}
`
